import { createContext, useContext, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { setConfig } from '../slices/configSlice';

import { settings } from '../config.js';
import { applyThemeCssVars } from '../config/themePalette';
import { getColor, getItemFromStore } from '../utils/utils';
import useToggleStylesheet from '../hooks/useToggleStyle';
import AppReleaseReload from '../components/common/AppReleaseReload';

const dismissAppBoot = () => {
	document.getElementById('app-boot')?.remove();
	document.documentElement.style.removeProperty('background');
	document.body.style.removeProperty('background');
};

export const AppContext = createContext(settings);

const AppProvider = ({ children }) => {
	const dispatch = useDispatch();
	const reduxIsDark = useSelector((state) => state.config?.isDark);
	const [isDark] = useState(getItemFromStore('isDark', settings.isDark));
	const [theme, setTheme] = useState(getItemFromStore('theme', settings.theme));
	const [isFluid] = useState(getItemFromStore('isFluid', settings.isFluid));
	const [isNavbarVerticalCollapsed] = useState(getItemFromStore('isNavbarVerticalCollapsed', settings.isNavbarVerticalCollapsed));

	const { isLoaded } = useToggleStylesheet(false, isDark);

	useEffect(() => {
		const isDarkInit = theme === 'auto' ? window.matchMedia('(prefers-color-scheme: dark)').matches : theme === 'dark';
		document.documentElement.setAttribute('data-bs-theme', isDarkInit ? 'dark' : 'light');
		applyThemeCssVars({ isLight: !isDarkInit });
		dispatch(setConfig({ key: 'isDark', value: isDarkInit }));
		dispatch(setConfig({ key: 'theme', value: theme }));
	}, [dispatch, theme]);

	useEffect(() => {
		if (!isLoaded) return undefined;
		dismissAppBoot();
		return undefined;
	}, [isLoaded]);

	const changeTheme = (theme) => {
		const isDarkChange = theme === 'auto' ? window.matchMedia('(prefers-color-scheme: dark)').matches : theme === 'dark';

		document.documentElement.setAttribute('data-bs-theme', isDarkChange ? 'dark' : 'light');
		applyThemeCssVars({ isLight: !isDarkChange });

		dispatch(setConfig({ key: 'isDark', value: isDarkChange }));
		dispatch(setConfig({ key: 'theme', value: theme }));
		setTheme(theme);
	};

	const getThemeColor = (name) => getColor(name);

	if (!isLoaded) {
		return null;
	}

	const toastTheme = reduxIsDark ? 'dark' : 'light';

	return (
		<AppContext.Provider value={{ isFluid, isNavbarVerticalCollapsed, changeTheme, getThemeColor }}>
			{children}
			<AppReleaseReload />
			<ToastContainer
				position='bottom-left'
				autoClose={3000}
				hideProgressBar={true}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss={false}
				draggable={false}
				pauseOnHover={false}
				theme={toastTheme}
			/>
		</AppContext.Provider>
	);
};

AppProvider.prototype = { children: PropTypes.node };

export const useAppContext = () => useContext(AppContext);

export default AppProvider;
